import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import Select from 'react-select'

import PosSearchSelect from 'pages/parts/tasks/PosSearchSelect'

import { httpOk, GET, POST, PUT } from 'helpers/api'
import { formatDate, parseLocalDateInput } from 'helpers/helpers'

// Форма звонка. Одна на два экрана: «Забота о клиентах» открывает её с уже выбранной
// точкой, журнал звонков — с пустой, и там точку выбирают поиском.
//
// Не дозвонились — тоже звонок, и записать его обязательно: без этой отметки
// непонятно, почему точка молчит вторую неделю — то ли её не обзванивали, то ли
// она не берёт трубку.
//
// Текст по-русски и без t(): админка не переводится (правило проекта).

const DIRECTIONS = [
	{ id: 'out', name: 'Мы звонили' },
	{ id: 'in', name: 'Нам звонили' },
]

function getDefaultItem() {
	return {
		'pos_id': null,
		'pos_employee_id': null,
		'contact_name': '',
		'phone': '',
		'direction': 'out',
		'topic_id': null,
		'is_answered': true,
		'has_problem': false,
		'comment': '',
		'called_at': formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss'),
		'outcome_id': null,
		'next_call_at': null,
		'promised_pay_at': null,
		'promised_amount': '',
		'churn_reason_id': null,
		'create_training_task': true,
		// Отчёт в телеграм-группу клиента: директор должен знать, что его люди к нам обращались
		'send_report': true,
	}
}

function addDays(days) {
	const date = new Date()
	date.setDate(date.getDate() + days)
	return formatDate(date, 'yyyy-MM-dd')
}

// Справочники итогов и причин общие для всех открытий формы — грузим один раз
let outcomesCache = null
let reasonsCache = null

function ModalCall({ show, onHide, pos = null, call = null, topics = [], onSaved }) {
	const [selectedItem, setSelectedItem] = useState(getDefaultItem())
	const [selectedPos, setSelectedPos] = useState(null)
	const [employees, setEmployees] = useState([])
	const [outcomes, setOutcomes] = useState(outcomesCache || [])
	const [reasons, setReasons] = useState(reasonsCache || [])
	// Телеграм-группы бренда, куда бот Mison Manager отправит отчёт о звонке
	const [reportChats, setReportChats] = useState([])

	const outcome = outcomes.find((item) => item.id === selectedItem.outcome_id) || null

	async function getDictionaries() {
		if (!outcomesCache) {
			const response = await GET('/admin/helper/call-outcomes', {}, { loader: false })
			if (httpOk(response)) {
				outcomesCache = response.data || []
				setOutcomes(outcomesCache)
			}
		}
		if (!reasonsCache) {
			const response = await GET('/admin/helper/churn-reasons', {}, { loader: false })
			if (httpOk(response)) {
				reasonsCache = response.data || []
				setReasons(reasonsCache)
			}
		}
	}

	// Итог задаёт, что ещё спросить. «Не дозвонились» сразу ставит перезвон на завтра —
	// иначе недозвон тихо выпадает из обзвона; оператор может поменять дату
	function selectOutcome(item) {
		const next = { ...selectedItem, 'outcome_id': item.id, 'is_answered': !!item.is_answered }

		// Дату, подставленную прошлым итогом, при смене итога убираем; выбранную
		// оператором руками — оставляем
		if (selectedItem.next_call_auto) {
			next.next_call_at = null
			next.next_call_auto = false
		}
		if (item.default_next_days && !next.next_call_at) {
			next.next_call_at = addDays(Number(item.default_next_days))
			next.next_call_auto = true
		}
		if (item.code === 'tech_problem') next.has_problem = true
		setSelectedItem(next)
	}

	// Телефоны выбранного контакта: у него их два, и оператор мог звонить на любой
	const employee = employees.find((item) => item.id === selectedItem.pos_employee_id)
	const phoneOptions = employee
		? [employee.phone, employee.phone_second].filter((item) => !!item)
		: []

	async function getEmployees(posId) {
		getReportChats(posId)

		if (!posId) {
			setEmployees([])
			return
		}

		const response = await GET(`/admin/pos/${posId}/employees`, {}, { loader: false })
		if (httpOk(response)) setEmployees(response.data || [])
	}

	async function getReportChats(posId) {
		setReportChats([])
		if (!posId) return

		const response = await GET('/admin/pos-calls/report-chats', { 'pos_id': posId }, { loader: false })
		if (httpOk(response)) setReportChats(response.data || [])
	}

	function handleSelectPos(option) {
		setSelectedPos(option)
		setSelectedItem({
			...selectedItem,
			'pos_id': option ? option.id : null,
			'pos_employee_id': null,
			'contact_name': '',
			'phone': '',
		})
		getEmployees(option ? option.id : null)
	}

	// Выбрали контакт — подставляем его телефон: оператор почти всегда звонит
	// на первый номер, и заставлять его выбирать ещё раз незачем
	function handleSelectEmployee(option) {
		setSelectedItem({
			...selectedItem,
			'pos_employee_id': option ? option.id : null,
			'contact_name': option ? option.full_name : '',
			'phone': option ? (option.phone || option.phone_second || '') : '',
		})
	}

	async function createUpdate(e) {
		e.preventDefault()

		var payload = {
			'pos_id': selectedItem.pos_id,
			'pos_employee_id': selectedItem.pos_employee_id,
			'contact_name': selectedItem.contact_name,
			'phone': selectedItem.phone,
			'direction': selectedItem.direction,
			'topic_id': selectedItem.topic_id,
			'is_answered': selectedItem.is_answered,
			'has_problem': selectedItem.has_problem,
			'comment': selectedItem.comment,
			'called_at': selectedItem.called_at,
			'outcome_id': selectedItem.outcome_id,
			'next_call_at': selectedItem.next_call_at,
			'promised_pay_at': selectedItem.promised_pay_at,
			'promised_amount': selectedItem.promised_amount,
			'churn_reason_id': selectedItem.churn_reason_id,
			'create_training_task': selectedItem.create_training_task,
			'send_report': !!selectedItem.send_report,
		}

		var response
		if (!selectedItem.id) {
			response = await POST('/admin/pos-calls', payload, { loader: true })
		} else {
			response = await PUT('/admin/pos-calls', { ...payload, 'id': selectedItem.id }, { loader: true })
		}

		if (httpOk(response)) {
			onHide()
			if (typeof onSaved === 'function') onSaved()
		}
	}

	useEffect(() => {
		if (!show) return

		getDictionaries()

		// Редактирование — поднимаем запись; создание — точка либо пришла со страницы,
		// либо её ещё выберут поиском
		if (call) {
			setSelectedItem({
				...getDefaultItem(),
				...call,
				'promised_amount': call.promised_amount ?? '',
				// Старый звонок без отчёта при правке в группу не уходит, пока оператор сам не включит
				'send_report': !!call.is_report_enabled,
			})
			setSelectedPos(null)
			getEmployees(call.pos_id)
			return
		}

		setSelectedItem({ ...getDefaultItem(), 'pos_id': pos ? pos.id : null })
		setSelectedPos(null)
		getEmployees(pos ? pos.id : null)
	}, [show, call, pos]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal show={show} animation={false} centered backdrop="static" size="lg" onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>
					{selectedItem.id ? 'Звонок' : 'Новый звонок'}
					{pos &&
						<span className="ms-2 text-muted fs-6">{pos.name} · {pos.login}</span>
					}
				</Modal.Title>
			</Modal.Header>
			<form onSubmit={createUpdate} autoComplete="off">
				<Modal.Body>
					{/* Верх формы — две строки: точка и кто звонил, затем когда, номер и с кем.
						Точку выбирают только когда модалку открыли из журнала: со страницы
						«Забота о клиентах» она уже известна и менять её незачем */}
					<div className="row">
						{!pos && !selectedItem.id &&
							<div className="col-md-7">
								<div className="form-group">
									<label>Точка <span className="text-danger">*</span></label>
									<PosSearchSelect
										apiPrefix="/admin"
										value={selectedPos}
										onChange={(option) => handleSelectPos(option)}
										placeholder="Название, ID или логин"
									/>
								</div>
							</div>
						}
						<div className="col-md-5">
							<div className="form-group">
								<label>Кто звонил <span className="text-danger">*</span></label>
								<div className="d-flex gap-2">
									{DIRECTIONS.map((item) => (
										<button type="button" key={item.id}
											className={`btn w-100 text-nowrap ${selectedItem.direction === item.id ? 'btn-primary' : 'btn-outline-primary'}`}
											onClick={() => setSelectedItem({ ...selectedItem, 'direction': item.id })}>
											{item.name}
										</button>
									))}
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col-md-3">
							<div className="form-group">
								<label>Дата и время</label>
								<div className="calendar-input">
									<DatePicker
										className="form-control"
										showTimeSelect
										timeFormat="HH:mm"
										timeIntervals={15}
										dateFormat="dd.MM.yyyy HH:mm"
										maxDate={new Date()}
										selected={parseLocalDateInput(selectedItem.called_at) || ''}
										onChange={(date) => setSelectedItem({
											...selectedItem,
											'called_at': formatDate(date, 'yyyy-MM-dd HH:mm:ss'),
										})} />
									<i className="uil uil-calendar-alt"></i>
								</div>
							</div>
						</div>
						<div className="col-md-3">
							<div className="form-group">
								<label>Телефон</label>
								<input type="text" className="form-control"
									placeholder="998901234567"
									value={selectedItem.phone || ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'phone': e.target.value })} />

								{/* У контакта два номера — переключаем в один клик, а не перенабираем */}
								{phoneOptions.length > 1 &&
									<div className="d-flex flex-wrap gap-1 mt-1">
										{phoneOptions.map((phone) => (
											<button type="button" key={phone}
												className={`btn btn-sm ${selectedItem.phone === phone ? 'btn-primary' : 'btn-outline-secondary'}`}
												onClick={() => setSelectedItem({ ...selectedItem, 'phone': phone })}>
												{phone}
											</button>
										))}
									</div>
								}
							</div>
						</div>
						{/* Выбрали контакт — поле «кто это был» не нужно, контакт занимает его место */}
						<div className={selectedItem.pos_employee_id ? 'col-md-6' : 'col-md-3'}>
							<div className="form-group">
								<label>С кем говорили</label>
								<Select
									isClearable
									options={employees}
									value={employees.find((item) => item.id === selectedItem.pos_employee_id) || ''}
									onChange={(option) => handleSelectEmployee(option)}
									placeholder="Контакт точки"
									noOptionsMessage={() => 'У точки нет контактов'}
									getOptionLabel={(option) => option.full_name + (option.position_name ? ' · ' + option.position_name : '')}
									getOptionValue={(option) => option.id}
								/>
							</div>
						</div>
						{/* Позвонил кто-то, кого в контактах нет: имя пишем руками,
							а в справочник его заводят отдельно, из карточки точки */}
						{!selectedItem.pos_employee_id &&
							<div className="col-md-3">
								<div className="form-group">
									<label>Или кто это был</label>
									<input type="text" className="form-control"
										placeholder="Имя"
										value={selectedItem.contact_name || ''}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'contact_name': e.target.value })} />
								</div>
							</div>
						}
					</div>

					<div className="form-group">
						<label>Тема</label>
						<Select
							isClearable
							options={topics}
							value={topics.find((item) => item.id === selectedItem.topic_id) || ''}
							onChange={(option) => setSelectedItem({ ...selectedItem, 'topic_id': option ? option.id : null })}
							placeholder="Выберите тему"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>

					<div className="form-group">
						<label>О чём говорили</label>
						<textarea className="form-control" rows="3"
							placeholder="Что сказал клиент, о чём договорились"
							value={selectedItem.comment || ''}
							onChange={(e) => setSelectedItem({ ...selectedItem, 'comment': e.target.value })}></textarea>
					</div>

					{/* Итог — из списка, а не текстом: по нему считается, сколько обещали
						оплатить и почему уходят. От итога зависит, что спросить дальше */}
					<div className="form-group">
						<label>Итог звонка {!selectedItem.id && <span className="text-danger">*</span>}</label>
						<div className="d-flex flex-wrap gap-2">
							{outcomes.map((item) => (
								<button type="button" key={item.id}
									className={`btn btn-sm ${selectedItem.outcome_id === item.id ? 'btn-primary' : 'btn-outline-primary'}`}
									onClick={() => selectOutcome(item)}>
									{item.name}
								</button>
							))}
						</div>
					</div>

					{outcome?.needs_promise &&
						<div className="row">
							<div className="col-md-6">
								<div className="form-group">
									<label>Обещали оплатить до <span className="text-danger">*</span></label>
									<div className="calendar-input">
										<DatePicker
											className="form-control"
											dateFormat="dd.MM.yyyy"
											minDate={new Date()}
											selected={parseLocalDateInput(selectedItem.promised_pay_at) || ''}
											onChange={(date) => setSelectedItem({ ...selectedItem, 'promised_pay_at': date ? formatDate(date, 'yyyy-MM-dd') : null })} />
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
							</div>
							<div className="col-md-6">
								<div className="form-group">
									<label>Сколько обещали</label>
									<input type="number" min="0" className="form-control"
										placeholder="Сумма, сум"
										value={selectedItem.promised_amount}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'promised_amount': e.target.value })} />
								</div>
							</div>
						</div>
					}

					{outcome?.needs_reason &&
						<div className="form-group">
							<label>Причина ухода <span className="text-danger">*</span></label>
							<Select
								options={reasons}
								value={reasons.find((item) => item.id === selectedItem.churn_reason_id) || ''}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'churn_reason_id': option ? option.id : null })}
								placeholder="Почему уходят"
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					}

					{/* Обучает тот, кто подключал: задача сама падает агенту точки на доску */}
					{outcome?.code === 'needs_training' && (
						selectedItem.task_id
							? <div className="form-group text-muted">Задача на обучение уже создана: №{selectedItem.task_id}</div>
							: <div className="form-check form-switch mb-3">
								<input type="checkbox" className="form-check-input" id="callTrainingTask"
									checked={!!selectedItem.create_training_task}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'create_training_task': e.target.checked })} />
								<label className="form-check-label" htmlFor="callTrainingTask">
									Создать задачу «Обучение» агенту точки
								</label>
							</div>
					)}

					<div className="row">
						<div className="col-md-6">
							<div className="form-group">
								<label>
									Перезвонить
									{outcome?.needs_next_call && <span className="text-danger"> *</span>}
								</label>
								<div className="calendar-input">
									<DatePicker
										className="form-control"
										dateFormat="dd.MM.yyyy"
										minDate={new Date()}
										isClearable
										placeholderText="Не нужно"
										selected={parseLocalDateInput(selectedItem.next_call_at) || ''}
										onChange={(date) => setSelectedItem({ ...selectedItem, 'next_call_at': date ? formatDate(date, 'yyyy-MM-dd') : null, 'next_call_auto': false })} />
									<i className="uil uil-calendar-alt"></i>
								</div>
								<div className="d-flex gap-1 mt-1">
									{[[1, 'завтра'], [3, 'через 3 дня'], [7, 'через неделю']].map(([days, title]) => (
										<button type="button" key={days} className="btn btn-sm btn-outline-secondary"
											onClick={() => setSelectedItem({ ...selectedItem, 'next_call_at': addDays(days), 'next_call_auto': false })}>
											{title}
										</button>
									))}
								</div>
							</div>
						</div>
					</div>

					<div className="d-flex flex-wrap gap-4">
						{/* Без итога (старый звонок) дозвон отмечают руками, с итогом его задаёт итог */}
						{!outcome &&
							<div className="form-check form-switch mb-0">
								<input type="checkbox" className="form-check-input" id="callAnswered"
									checked={!!selectedItem.is_answered}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'is_answered': e.target.checked })} />
								<label className="form-check-label" htmlFor="callAnswered">Дозвонились</label>
							</div>
						}

						<div className="form-check form-switch mb-0">
							<input type="checkbox" className="form-check-input" id="callProblem"
								checked={!!selectedItem.has_problem}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'has_problem': e.target.checked })} />
							<label className="form-check-label" htmlFor="callProblem">Всплыла проблема</label>
						</div>

						{/* Отчёт в группу клиента: бот пишет точку, с кем говорили, тему и «о чём».
							Итог, обещания и причины ухода туда не попадают — это внутреннее.
							Без подписей под галочкой: почему она недоступна и куда уйдёт — в подсказке */}
						<div className="form-check form-switch mb-0"
							title={!selectedItem.is_answered
								? 'Недозвон в группу не отправляется'
								: selectedItem.pos_id && reportChats.length === 0
									? 'У бренда нет группы с ботом @mison_manager_bot'
									: reportChats.map((item) => item.title || item.chat_id).join(', ')}>
							<input type="checkbox" className="form-check-input" id="callSendReport"
								disabled={!selectedItem.is_answered || (!!selectedItem.pos_id && reportChats.length === 0)}
								checked={!!selectedItem.send_report && !!selectedItem.is_answered && (!selectedItem.pos_id || reportChats.length > 0)}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'send_report': e.target.checked })} />
							<label className="form-check-label" htmlFor="callSendReport">Отправить отчёт в группу клиента</label>
						</div>
					</div>

				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-warning" onClick={onHide}>Отмена</button>
					<button type="submit" className="btn btn-primary"
						disabled={(!selectedItem.pos_id && !selectedItem.id) || (!selectedItem.id && !selectedItem.outcome_id)}>
						Сохранить
					</button>
				</Modal.Footer>
			</form>
		</Modal>
	)
}

export default ModalCall
